<template>
  <div style="margin:20px">
    <FiPageRail title="Vehicles by Yard" nowrap-actions>
      <template slot="actions">
        <b-button size="sm" variant="outline-secondary" :disabled="loading || !yards.length"
                  @click.prevent="download_csv()" aria-label="Download" title="Download this report as a CSV file">
          <i class="fa fa-download" aria-hidden="true"></i>
        </b-button>
        <b-button size="sm" variant="outline-secondary" v-b-modal.yard-info
                  aria-label="About this page" title="How this page works, and how to set up the nightly email">
          <i class="fa fa-info-circle" aria-hidden="true"></i>
        </b-button>
      </template>
    </FiPageRail>

    <b-modal id="yard-info" title="About this page" ok-only ok-title="Close" size="lg">
      <p>Each night at midnight &mdash; your own local time, not ours &mdash; we record where every
        vehicle is parked and keep it. The <b>Snapshot</b> picker chooses which night you are
        looking at, with the capture time shown beside each one, or <b>Live</b> for where
        everything is right this minute.</p>

      <p><b>How a vehicle is counted.</b> It counts for the yard it is <i>closest</i> to, within
        half a mile. Yards nearer to each other than that still come out right, because the nearest
        one wins rather than every yard the vehicle happens to be near.</p>

      <p><b>The maps.</b> Click any yard for the full map and its vehicle list. The boundary drawn
        in your GPS system is shaded green where one exists, each vehicle is an orange square, and
        the half-mile circle is shown so you can see why a vehicle was counted. Vehicles often sit
        just outside the drawn boundary &mdash; on the street, in overflow, at the pumps &mdash;
        and they still count.</p>

      <p><b>Geofence: No</b> means your GPS system has no boundary drawn at that yard, or the only
        one with that name is somewhere else entirely, so we ignore it. It does not affect the
        count &mdash; the vehicle positions are the same either way, you just lose the outline on
        the map.</p>

      <p>Two yards set up at the same address appear as one row carrying both names, so a single
        site is not split across two lines. Click any column heading to sort.</p>

      <p><b>Getting the list out.</b> The download button at the top right gives you a CSV with the
        yard totals first and then a row per vehicle. Inside an open yard, the copy button puts that
        yard's fleet numbers on your clipboard.</p>

      <p><b>Emailing it out.</b> To have the same CSV sent automatically, add the addresses under
        <b>Settings &rarr; Views &amp; Reports &rarr; Vehicles by Yard Report</b>, separated by
        commas, and set the day and hour beneath them. Leave the addresses blank and nothing is
        sent.</p>
    </b-modal>

    <div class="controls d-flex align-items-center justify-content-center">
      Snapshot&nbsp;<b-form-select v-model="snapshot_date" :options="date_options" :disabled="loading" style="font-size: 16px; font-weight: bold; width:250px"/>
      <span v-if="!loading && totals.vehicles != null" class="sep">|</span>
      <span v-if="!loading && totals.vehicles != null" class="stats">
        <span class="stat-chip"><b>{{ totals.yards || 0 }}</b> yards</span>
        <span class="stat-chip"><b>{{ totals.vehicles || 0 }}</b> vehicles</span>
        <span class="stat-chip"><b>{{ totals.in_yard || 0 }}</b> in a yard</span>
        <span class="stat-chip"><b>{{ totals.on_road || 0 }}</b> on the road</span>
      </span>
    </div>

    <div v-if="!loading">

      <div v-if="!yards.length" style="margin:30px; font-size: 18px;">
        No snapshot has been captured for this date yet.
      </div>

      <table v-else-if="!is_mobile" border="1" cellpadding="2" class="yard-table">
        <tr>
          <th width="420px" class="sortable" @click="sort_by('yard_name')">Yard Name{{ sort_arrow('yard_name') }}</th>
          <th width="420px" class="sortable col-addr" @click="sort_by('address')">Yard Address{{ sort_arrow('address') }}</th>
          <th width="90px" class="sortable" @click="sort_by('count')">Vehicles{{ sort_arrow('count') }}</th>
          <th width="100px" class="sortable col-fence" @click="sort_by('geofence')" title="Does the telematics provider have a boundary drawn for this yard?">Geofence{{ sort_arrow('geofence') }}</th>
          <!-- last column, so it takes the leftover width; the image inside stays 300px and sits
               left, which keeps Yard narrow without an empty spacer column drawing a border -->
          <th class="map-col">Click for Full Map View</th>
        </tr>
        <template v-for="yard in yards">
          <tr :key="yard.yard_name" @click="toggle(yard.yard_name)" style="cursor: pointer">
            <td class="yard-name">
              <span class="chev">{{ expanded[yard.yard_name] ? '&#9660;' : '&#9654;' }}</span>
              <b>{{ yard.yard_name }}</b>
            </td>
            <td class="addr col-addr">{{ yard.address }}</td>
            <td align="center">{{ yard.count }}</td>
            <td align="center" class="col-fence" :class="has_geofence(yard) ? '' : 'muted'">{{ has_geofence(yard) ? 'Yes' : 'No' }}</td>
            <td class="map-col">
              <img v-if="yard_preview_url(yard)" :src="yard_preview_url(yard)" loading="lazy"
                   class="yard-preview" width="300" height="170" :alt="preview_alt(yard)"/>
              <span v-else class="muted">empty</span>
            </td>
          </tr>
          <tr v-if="expanded[yard.yard_name]" :key="yard.yard_name + '-map'">
            <td colspan="5" style="padding:0">
              <div class="yard-detail">
                <GmapMap :zoom="map_zoom(yard)" :center="map_center(yard)" :options="map_options"
                         :style="{width: '100%', height: map_height}">
                  <GmapPolygon v-for="(p, i) in yard_geofences(yard)" :key="'g' + i"
                               :paths="p" :options="geofence_options"/>
                  <GmapCircle v-for="(p, i) in yard_points(yard)" :key="'c' + i"
                              :center="{lat: Number(p.lat), lng: Number(p.lon)}"
                              :radius="(Number(yard.radius_miles) || 0.5) * 1609.34"
                              :options="circle_options"/>
                  <GmapMarker v-for="(v, i) in yard.vehicles" :key="'v' + i"
                              :position="{lat: Number(v.lat), lng: Number(v.lon)}"
                              :label="{text: String(v.name), fontSize: '11px', fontWeight: 'bold'}"
                              :title="marker_title(v)"/>
                </GmapMap>
                <div class="yard-legend">
                  <span class="key key-fence"></span> drawn geofence
                  <span class="key key-circle"></span> counted for this yard ({{ yard.radius_miles || 0.5 }} mi)
                  <span v-if="!yard_geofences(yard).length" class="muted">&mdash; no boundary drawn for this yard</span>
                </div>
                <div class="yard-fleet">
                  <b-button size="sm" variant="outline-secondary" class="copy-btn"
                            :title="'Copy all ' + yard.count + ' fleet numbers'"
                            @click.stop="copy_list(yard.vehicles, yard.yard_name)">
                    <i class="fa fa-clone" aria-hidden="true"></i>
                    <span class="sr-only">Copy the fleet numbers for {{ yard.yard_name }}</span>
                  </b-button>
                  <span>{{ yard.vehicles.map(v => v.name).join(', ') }}</span>
                </div>
              </div>
            </td>
          </tr>
        </template>
        <tr v-if="on_road.count" @click="toggle('__on_road__')" style="cursor: pointer">
          <td class="yard-name">
            <span class="chev">{{ expanded['__on_road__'] ? '&#9660;' : '&#9654;' }}</span>
            <b><i>On the road</i></b>
          </td>
          <td class="addr muted col-addr">&mdash;</td>
          <td align="center">{{ on_road.count }}</td>
          <td align="center" class="muted col-fence">&mdash;</td>
          <td class="map-col">
            <img v-if="road_preview_url" :src="road_preview_url" loading="lazy"
                 class="yard-preview" width="300" height="170"
                 :alt="on_road.count + ' vehicles that were not at any yard'"/>
            <span v-else class="muted">&nbsp;click to list {{ on_road.count }}</span>
          </td>
        </tr>
        <tr v-if="on_road.count && expanded['__on_road__']">
          <td colspan="5" style="padding:0">
            <div class="yard-detail">
              <GmapMap :zoom="map_zoom(on_road_group())" :center="map_center(on_road_group())"
                       :options="road_map_options" :style="{width: '100%', height: map_height}">
                <GmapMarker v-for="(p, i) in yard_pins()" :key="'yp' + i" :position="p.position"
                            :title="p.title" :icon="YARD_PIN"/>
                <GmapMarker v-for="(v, i) in on_road.vehicles" :key="'rv' + i"
                            :position="{lat: Number(v.lat), lng: Number(v.lon)}"
                            :label="{text: String(v.name), fontSize: '11px', fontWeight: 'bold'}"
                            :title="marker_title(v)"/>
              </GmapMap>
              <div class="yard-legend">
                <span class="key key-circle"></span> configured yard
                &nbsp;&mdash;&nbsp; a vehicle here was more than {{ road_radius }} mi from every yard when the
                snapshot was taken
              </div>
              <div class="yard-fleet">
                <b-button size="sm" variant="outline-secondary" class="copy-btn"
                          :title="'Copy all ' + on_road.count + ' fleet numbers'"
                          @click.stop="copy_list(on_road.vehicles, 'On the road')">
                  <i class="fa fa-clone" aria-hidden="true"></i>
                  <span class="sr-only">Copy the fleet numbers for vehicles on the road</span>
                </b-button>
                <span>{{ on_road.vehicles.map(v => v.name).join(', ') }}</span>
              </div>
            </div>
          </td>
        </tr>
      </table>

      <div v-else class="cards">
        <template v-for="yard in yards">
          <div class="yard-card" :key="yard.yard_name" @click="toggle(yard.yard_name)">
            <div class="card-title">
              <span class="chev">{{ expanded[yard.yard_name] ? '&#9660;' : '&#9654;' }}</span>{{ yard.yard_name }}
            </div>
            <div class="card-row">
              <div class="card-body">
                <div class="card-addr">{{ yard.address }}</div>
                <div class="card-meta">
                  <b>{{ yard.count }}</b> vehicles
                  <span :class="has_geofence(yard) ? '' : 'muted'">&middot; geofence {{ has_geofence(yard) ? 'yes' : 'no' }}</span>
                </div>
              </div>
              <img v-if="yard_preview_url(yard, 150, 85)" :src="yard_preview_url(yard, 150, 85)"
                   loading="lazy" class="card-thumb" width="150" height="85" :alt="preview_alt(yard)"/>
              <div v-else class="card-thumb card-thumb-empty">empty</div>
            </div>
          </div>
          <div v-if="expanded[yard.yard_name]" :key="yard.yard_name + '-card-map'" class="card-detail">
            <GmapMap :zoom="map_zoom(yard)" :center="map_center(yard)" :options="map_options"
                     :style="{width: '100%', height: map_height}">
              <GmapPolygon v-for="(p, i) in yard_geofences(yard)" :key="'cg' + i"
                           :paths="p" :options="geofence_options"/>
              <GmapCircle v-for="(p, i) in yard_points(yard)" :key="'cc' + i"
                          :center="{lat: Number(p.lat), lng: Number(p.lon)}"
                          :radius="(Number(yard.radius_miles) || 0.5) * 1609.34"
                          :options="circle_options"/>
              <GmapMarker v-for="(v, i) in yard.vehicles" :key="'cv' + i"
                          :position="{lat: Number(v.lat), lng: Number(v.lon)}"
                          :label="{text: String(v.name), fontSize: '11px', fontWeight: 'bold'}"
                          :title="marker_title(v)"/>
            </GmapMap>
            <div class="yard-fleet">
              <b-button size="sm" variant="outline-secondary" class="copy-btn"
                        :title="'Copy all ' + yard.count + ' fleet numbers'"
                        @click.stop="copy_list(yard.vehicles, yard.yard_name)">
                <i class="fa fa-clone" aria-hidden="true"></i>
              </b-button>
              <span>{{ yard.vehicles.map(v => v.name).join(', ') }}</span>
            </div>
          </div>
        </template>

        <div v-if="on_road.count" class="yard-card" @click="toggle('__on_road__')">
          <div class="card-title">
            <span class="chev">{{ expanded['__on_road__'] ? '&#9660;' : '&#9654;' }}</span><i>On the road</i>
          </div>
          <div class="card-row">
            <div class="card-body">
              <div class="card-meta"><b>{{ on_road.count }}</b> vehicles</div>
            </div>
            <img v-if="road_preview_card_url" :src="road_preview_card_url" loading="lazy"
                 class="card-thumb" width="150" height="85"
                 :alt="on_road.count + ' vehicles that were not at any yard'"/>
          </div>
        </div>
        <div v-if="on_road.count && expanded['__on_road__']" class="card-detail">
          <GmapMap :zoom="map_zoom(on_road_group())" :center="map_center(on_road_group())"
                   :options="road_map_options" :style="{width: '100%', height: map_height}">
            <GmapMarker v-for="(p, i) in yard_pins()" :key="'cyp' + i" :position="p.position"
                        :title="p.title" :icon="YARD_PIN"/>
            <GmapMarker v-for="(v, i) in on_road.vehicles" :key="'crv' + i"
                        :position="{lat: Number(v.lat), lng: Number(v.lon)}"
                        :label="{text: String(v.name), fontSize: '11px', fontWeight: 'bold'}"
                        :title="marker_title(v)"/>
          </GmapMap>
          <div class="yard-fleet">
            <b-button size="sm" variant="outline-secondary" class="copy-btn"
                      :title="'Copy all ' + on_road.count + ' fleet numbers'"
                      @click.stop="copy_list(on_road.vehicles, 'On the road')">
              <i class="fa fa-clone" aria-hidden="true"></i>
            </b-button>
            <span>{{ on_road.vehicles.map(v => v.name).join(', ') }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
  </div>
</template>

<script>
import {get_tbn_vehicles_by_yard_report} from '../networking/tbn_apis'

// the picker's live entry; not a date, so it can never collide with a snapshot_date
const LIVE = '__live__'
// same key the maps js api is loaded with in main.js, so the image exposes nothing new
const GOOGLE_MAPS_KEY = 'AIzaSyDO2Uu3jHZqAIgXw9jA0e0Wqi8q0-4C5Qs'
// google's documented static map url limit is 16384; a 12.6k url was verified to return 200, so
// stay comfortably inside that rather than at the old 8k guess
const MAX_STATIC_MAP_URL = 12500
// half-height of a vehicle square in degrees latitude (~5m). the longitude half-width is scaled
// by 1/cos(lat) so it comes out square on screen rather than stretched
const VEHICLE_SQUARE_DEG = 0.00005
// cell sizes, in degrees, for collapsing overlapping buses until the url fits: ~1m up to ~90m
const PREVIEW_GRIDS = [0.00001, 0.00003, 0.00006, 0.0001, 0.0002, 0.0004, 0.0008]
import {mapState} from "vuex"
import moment from 'moment'

export default {
  name: 'vehicles_by_yard_tbn',

  components: {
  },

  data() {
    return {
      loading: false,
      live: false,
      snapshot_date: null,
      available_dates: [],
      yards: [],
      on_road: {count: 0, vehicles: []},
      totals: {},
      mode: 'snapshot',
      sort_col: null,
      sort_desc: true,
      expanded: {},
      map_height: '560px',
      is_mobile: false,
      // satellite + tilt 0, matching the yard map behind Settings > General > Manage Yards
      map_options: {streetViewControl: false, mapTypeId: 'satellite', tilt: 0, fullscreenControl: true},
      // the strays span the whole operating area, so a road map reads better than satellite here
      road_map_options: {streetViewControl: false, mapTypeId: 'roadmap', fullscreenControl: true},
      YARD_PIN: 'https://maps.google.com/mapfiles/ms/icons/grey-dot.png',
      geofence_options: {
        strokeColor: '#39b54a', strokeOpacity: 0.95, strokeWeight: 2,
        fillColor: '#39b54a', fillOpacity: 0.12, clickable: false,
      },
      // the circle is the rule this report actually applied -- nearest yard within radius_miles --
      // not a geofence anyone drew. drawing a boundary we did not use would misstate why a bus
      // counted for this yard.
      circle_options: {
        strokeColor: '#3a7bd5', strokeOpacity: 0.65, strokeWeight: 2,
        fillColor: '#3a7bd5', fillOpacity: 0.08, clickable: false,
      },
    }
  },

  created() {
    this.fit_to_viewport()
    window.addEventListener('resize', this.fit_to_viewport)
    this.load_data()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.fit_to_viewport)
  },

  methods: {
    async load_data() {
      this.loading = true
      try {
        const {value: {data = {}}} = await get_tbn_vehicles_by_yard_report(this.user.tenant, this.snapshot_date, this.live)
        this.available_dates = data.available_dates || []
        this.yards = data.yards || []
        this.on_road = data.on_road || {count: 0, vehicles: []}
        this.totals = data.totals || {}
        this.mode = data.mode
        // follow the server to whichever date it actually served, so the picker never claims a
        // date the snapshot did not come from
        if (!this.live && data.snapshot_date) this.snapshot_date = data.snapshot_date
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },

    has_geofence(yard) {
      return this.yard_geofences(yard).length > 0
    },

    sort_arrow(col) {
      if (this.sort_col !== col) return ''
      return this.sort_desc ? ' \u25BC' : ' \u25B2'
    },

    sort_by(col) {
      // name sorts A-Z first; counts and Yes/No sort highest-first, which is what you want to see
      this.sort_desc = this.sort_col === col ? !this.sort_desc : !['yard_name', 'address'].includes(col)
      this.sort_col = col
      const dir = this.sort_desc ? -1 : 1
      const byName = (a, b) => String(a.yard_name || '').localeCompare(String(b.yard_name || ''))
      this.yards = [...this.yards].sort((a, b) => {
        if (col === 'count') return (a.count - b.count) * dir || byName(a, b)
        if (col === 'address') {
          return String(a.address || '').localeCompare(String(b.address || '')) * dir || byName(a, b)
        }
        if (col === 'geofence') {
          return ((this.has_geofence(a) ? 1 : 0) - (this.has_geofence(b) ? 1 : 0)) * dir || byName(a, b)
        }
        return byName(a, b) * dir
      })
    },

    download_csv() {
      // two sections: the yard totals someone reads at a glance, then one row per vehicle so the
      // file can still be sorted, filtered and pivoted -- which is most of what an audit does
      // alphabetical in the file, whatever the page is sorted by: the page is a glance view and
      // defaults to busiest-first, but someone handed the csv is looking up a yard by name
      const sorted = [...this.yards].sort((a, b) =>
        String(a.yard_name || '').localeCompare(String(b.yard_name || '')))

      const rows = [['Yard', 'Vehicles']]
      sorted.forEach(y => rows.push([y.yard_name, y.count]))
      if (this.on_road.count) rows.push(['On the road', this.on_road.count])
      rows.push(['Total', sorted.reduce((n, y) => n + y.count, 0) + this.on_road.count])

      rows.push([])
      rows.push(['Yard', 'Vehicle'])
      sorted.forEach(y => {
        y.vehicles.forEach(v => rows.push([y.yard_name, v.name]))
      })
      this.on_road.vehicles.forEach(v => rows.push(['On the road', v.name]))
      const csv = rows.map(r => r.map(c => '"' + String(c == null ? '' : c).replace(/"/g, '""') + '"').join(',')).join('\n')
      const link = document.createElement('a')
      link.href = URL.createObjectURL(new Blob([csv], {type: 'text/csv'}))
      link.download = 'vehicles-by-yard-' + (this.snapshot_date || 'live') + '.csv'
      link.click()
      URL.revokeObjectURL(link.href)
    },

    async copy_list(vehicles, label) {
      const text = (vehicles || []).map(v => v.name).join(', ')
      try {
        // clipboard API needs a secure context; fall back for anyone on plain http
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          const el = document.createElement('textarea')
          el.value = text
          el.setAttribute('readonly', '')
          el.style.position = 'absolute'
          el.style.left = '-9999px'
          document.body.appendChild(el)
          el.select()
          document.execCommand('copy')
          document.body.removeChild(el)
        }
        this.$emit('showAlert', 'Copied ' + (vehicles || []).length + ' fleet numbers for ' + label, 'success', 2500)
      } catch (e) {
        this.$emit('showAlert', 'Could not copy to the clipboard', 'danger', 4000)
      }
    },

    fit_to_viewport() {
      // a table of five columns and a 300px image cannot be made to read on a phone, so the
      // narrow layout is a different component rather than the same one squeezed
      this.is_mobile = window.innerWidth <= 768
      this.map_height = this.is_mobile ? '300px' : '560px'
    },

    yard_clock(local_time) {
      // parseZone keeps the offset the capture was stamped with -- the tenant's own timezone.
      // moment()/.local() would re-render it in whoever is looking at the page.
      return local_time ? moment.parseZone(local_time) : null
    },

    snapshot_label(snapshot) {
      const day = moment(snapshot.snapshot_date, 'YYYY-MM-DD').format('ddd MMM D')
      const stamp = this.yard_clock(snapshot.local_time)
      return stamp ? day + '  ' + stamp.format('h:mm A') : day
    },

    toggle(key) {
      // Vue 2 cannot see a plain key added to an object, so replace the map rather than mutate it
      this.expanded = {...this.expanded, [key]: !this.expanded[key]}
    },

    yard_points(yard) {
      if (yard.points && yard.points.length) return yard.points
      return yard.lat == null ? [] : [{lat: yard.lat, lon: yard.lon}]
    },

    // the on-the-road group behaves like a yard row for the map helpers, minus a yard of its own
    on_road_group() {
      return {yard_name: 'On the road', points: [], vehicles: this.on_road.vehicles, radius_miles: 0}
    },

    // every configured yard as a faint pin: a stray is only interpretable next to the yard it
    // nearly reached, which is most of them
    yard_pins() {
      return (this.yards || [])
        .filter(y => y.lat != null)
        .map(y => ({position: {lat: Number(y.lat), lng: Number(y.lon)}, title: y.yard_name}))
    },

    preview_alt(yard) {
      return yard.count + ' vehicles at ' + yard.yard_name +
        (this.yard_geofences(yard).length ? ', shown against the drawn geofence' : ', no geofence drawn')
    },

    yard_preview_url(yard, width, height, maptype) {
      // a static map image rather than a live GmapMap: one row per yard means ~35 maps on a page,
      // and google bills a Dynamic Maps load per instance against a Static Maps request at roughly
      // a third of that. loading="lazy" on the img means rows below the fold never fetch at all.
      const buses = (yard.vehicles || []).filter(v => v.lat != null && v.lon != null)
      const fences = this.yard_geofences(yard)
      if (!buses.length && !fences.length) return ''

      const base = 'https://maps.googleapis.com/maps/api/staticmap?size=' +
        (width || 300) + 'x' + (height || 170) + '&scale=2&maptype=' + (maptype || 'satellite')
      const fenceParts = fences.map(path =>
        '&path=' + encodeURIComponent('color:0x39b54aff|weight:3|fillcolor:0x39b54a22|' +
          path.map(p => p.lat.toFixed(5) + ',' + p.lng.toFixed(5)).join('|')))

      // a square a few metres across is sub-pixel once the view spans a county, which is what the
      // on-the-road group does -- it covered 128 miles for DATTCO and rendered an empty state map.
      // markers are the wrong tool inside a yard (they merge into one orange mass) and the right
      // one out here, where the vehicles are miles apart.
      const b = this.map_extent(yard)
      const spanMiles = Math.max((b.north - b.south) * 69.17,
                                 (b.east - b.west) * 69.17 * Math.cos(b.north * Math.PI / 180))
      if (spanMiles > 3) {
        const pins = [...new Set(buses.map(v =>
          Number(v.lat).toFixed(4) + ',' + Number(v.lon).toFixed(4)))]
        const url = base + fenceParts.join('') +
          '&markers=' + encodeURIComponent('size:tiny|color:0xe8663a|' + pins.join('|')) +
          '&key=' + GOOGLE_MAPS_KEY
        if (url.length <= MAX_STATIC_MAP_URL) return url
      }

      // each bus is a closed four-corner square, filled solid with a thin white outline. a
      // two-point path was cheaper but its round line caps rendered as a blurred blob, and real
      // markers -- even size:tiny -- are ~20px teardrops that merge into one mass at this size.
      const dotsAt = places => places.map(c => {
        const [lat, lon] = c.split(',').map(Number)
        const dy = VEHICLE_SQUARE_DEG
        const dx = dy / Math.max(0.2, Math.cos(lat * Math.PI / 180))
        const corners = [[lat - dy, lon - dx], [lat - dy, lon + dx],
                         [lat + dy, lon + dx], [lat + dy, lon - dx]]
          .map(p => p[0].toFixed(5) + ',' + p[1].toFixed(5))
        return '&path=' + encodeURIComponent(
          'color:0xffffffff|weight:1|fillcolor:0xe8663aff|' + corners.join('|'))
      }).join('')
      const cellsOn = grid => [...new Set(buses.map(v =>
        (Math.round(Number(v.lat) / grid) * grid).toFixed(5) + ',' +
        (Math.round(Number(v.lon) / grid) * grid).toFixed(5)))]

      // buses parked side by side land in the same cell and would have overlapped anyway, so
      // collapsing them costs nothing visually while keeping the url inside the length limit.
      // stepped finely on purpose: decimal places jump 11m -> 110m, which took the busiest yard
      // from 112 squares straight down to 18 and made a full yard look nearly empty.
      const suffix = '&key=' + GOOGLE_MAPS_KEY
      const head = base + fenceParts.join('')
      for (const grid of PREVIEW_GRIDS) {
        const url = head + dotsAt(cellsOn(grid)) + suffix
        if (url.length <= MAX_STATIC_MAP_URL) return url
      }
      return head + suffix
    },

    yard_geofences(yard) {
      // only the boundaries the provider actually has drawn; 10 of DATTCO's 44 yards have none,
      // and the circle still explains why each bus was counted
      return this.yard_points(yard)
        .filter(p => p.geofence && p.geofence.length)
        .map(p => p.geofence.map(v => ({lat: Number(v.lat), lng: Number(v.lon)})))
    },

    map_extent(yard) {
      // everything that has to be on screen: the whole drawn boundary, every bus counted, and the
      // yard points themselves. deliberately NOT the assignment circle -- at 0.5 miles it is wider
      // than any of these yards, and framing it would shrink the boundary to a dot.
      const pts = []
      this.yard_points(yard).forEach(p => {
        pts.push({lat: Number(p.lat), lng: Number(p.lon)})
        ;(p.geofence || []).forEach(v => pts.push({lat: Number(v.lat), lng: Number(v.lon)}))
      })
      ;(yard.vehicles || []).forEach(v => {
        if (v.lat != null && v.lon != null) pts.push({lat: Number(v.lat), lng: Number(v.lon)})
      })
      const lats = pts.map(p => p.lat)
      const lngs = pts.map(p => p.lng)
      return {
        north: Math.max(...lats), south: Math.min(...lats),
        east: Math.max(...lngs), west: Math.min(...lngs),
      }
    },

    map_center(yard) {
      const b = this.map_extent(yard)
      return {lat: (b.north + b.south) / 2, lng: (b.east + b.west) / 2}
    },

    map_zoom(yard) {
      // fitBounds would need the map instance, and these maps are rendered in a v-for where the
      // refs arrive after the zoom is bound -- so derive it from the extent instead, which is
      // deterministic and testable. padded a notch so the boundary never touches the edge.
      const b = this.map_extent(yard)
      const W = 1100, H = 560, TILE = 256, MAX_ZOOM = 19
      const latRad = lat => {
        const s = Math.sin(lat * Math.PI / 180)
        return Math.log((1 + s) / (1 - s)) / 2
      }
      const latFraction = Math.abs(latRad(b.north) - latRad(b.south)) / (2 * Math.PI)
      let lngSpan = b.east - b.west
      if (lngSpan < 0) lngSpan += 360
      const lngFraction = lngSpan / 360
      // a single point (one bus, no boundary) has no extent to fit; fall back to a close-in view
      const zoomFor = (px, fraction) =>
        fraction > 0 ? Math.log(px / TILE / fraction) / Math.LN2 : MAX_ZOOM
      const zoom = Math.min(zoomFor(H, latFraction), zoomFor(W, lngFraction), MAX_ZOOM)
      return Math.max(1, Math.floor(zoom) - 1)
    },

    marker_title(vehicle) {
      const when = this.fix_time(vehicle)
      return vehicle.name + (when ? ' — last fix ' + when : '')
    },

    fix_time(vehicle) {
      // only the map marker tooltip now: how stale a position is matters when you are looking at
      // one bus, but it is noise in a list of 1,600
      return vehicle.time ? moment.utc(vehicle.time).local().format('YYYY-MM-DD HH:mm') : ''
    },
  },

  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    // roadmap, not satellite: these span the whole operating area, where satellite imagery shows
    // nothing but trees. computed rather than called inline so the url is built once per render.
    road_preview_url() {
      return this.on_road.count ? this.yard_preview_url(this.on_road_group(), 300, 170, 'roadmap') : ''
    },

    road_preview_card_url() {
      return this.on_road.count ? this.yard_preview_url(this.on_road_group(), 150, 85, 'roadmap') : ''
    },

    road_radius() {
      const y = (this.yards || [])[0]
      return (y && y.radius_miles) || 0.5
    },

    date_options() {
      // live leads the list: it is the only entry that is not a stored night, and putting it here
      // rather than beside the picker keeps one control answering "which moment am I looking at"
      return [{value: LIVE, text: 'Live \u2014 right now'}].concat(
        this.available_dates.map(d => ({value: d.snapshot_date, text: this.snapshot_label(d)})))
    },
  },

  watch: {
    user: {
      deep: true,
    },
    'user.tenant'(newTenant, oldTenant) {
      if (!oldTenant || newTenant === oldTenant) return
      // everything on the page belongs to the old tenant -- yards, snapshots, the open rows --
      // so clear it rather than leave one company's buses on screen under another's name
      this.yards = []
      this.on_road = {count: 0, vehicles: []}
      this.totals = {}
      this.available_dates = []
      this.expanded = {}
      this.live = false
      this.snapshot_date = null
      this.load_data()
    },
    // no watcher on `live`: show_live() loads explicitly so a second click refetches, where a
    // watcher would see no change and do nothing
    snapshot_date(val, old) {
      if (old === null) return
      this.live = val === LIVE
      this.load_data()
    },
  }
}
</script>

<style scoped>

tr:nth-child(even) {
  background-color: #f2f2f2 !important;
}

.sortable {
  cursor: pointer;
  user-select: none;
}


.controls {
  position: relative;
  font-weight: bold;
  font-size: 24px;
  flex-wrap: wrap;
  gap: 6px 0;
}

.sep {
  padding: 0 14px;
  opacity: 0.4;
}

.stats {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.stat-chip {
  border: 1px solid rgba(128, 128, 128, 0.45);
  border-radius: 14px;
  padding: 3px 12px;
  font-size: 13px;
  white-space: nowrap;
}

.stat-chip b {
  font-size: 15px;
}

.cards {
  margin: 14px 8px 0 8px;
  text-align: left;
}

/* the yard name runs the full width across the top -- it is what you scan for and it is the one
   thing that wraps -- then the detail fills the space to the left of the thumbnail */
.yard-card {
  padding: 4px 6px;
  border: 1px solid rgba(128, 128, 128, 0.35);
  border-radius: 4px;
  margin-bottom: 5px;
  cursor: pointer;
  /* the page centres its other blocks; a card is a record, so it reads left */
  text-align: left;
}

.card-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.card-row .card-body {
  flex: 1 1 auto;
}

.card-thumb {
  flex: 0 0 150px;
  border-radius: 3px;
  border: 1px solid rgba(128, 128, 128, 0.35);
  margin-top: 2px;
}

.card-thumb-empty {
  width: 150px;
  height: 85px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: italic;
  opacity: 0.5;
  font-size: 12px;
}

.card-body {
  min-width: 0;
}

.card-title {
  font-weight: bold;
  font-size: 15px;
  line-height: 1.25;
  word-break: break-word;
}

.card-addr {
  font-size: 12px;
  line-height: 1.3;
  opacity: 0.75;
  word-break: break-word;
}

.card-meta {
  font-size: 13px;
  line-height: 1.3;
  padding-top: 3px;
}

.card-detail {
  margin: 0 0 10px 0;
  text-align: left;
}

@media (max-width: 768px) {
  .controls {
    font-size: 15px;
    justify-content: flex-start !important;
  }

  /* the separators only make sense on one line */
  .sep {
    display: none;
  }

  .stat-chip {
    font-size: 12px;
    padding: 2px 9px;
  }
}

.addr {
  font-size: 13px;
  font-weight: normal;
}

/* the yard name and its address read as a label, not a figure -- the table's cellpadding
   centres them otherwise */
td.yard-name,
td.addr {
  text-align: left;
}

.chev {
  display: inline-block;
  width: 14px;
  font-size: 10px;
  opacity: 0.6;
}

.muted {
  opacity: 0.55;
  font-style: italic;
}

.yard-detail {
  padding: 8px 10px 10px 10px;
}

/* pin the column to the image: the preview is a fixed 300px static map, so the column should not
   stretch with it. Yard is the flexible column instead. */
/* the table no longer stretches to the page, so nothing needs a flexible column to absorb
   leftover width and this can be exactly the image it holds */
.yard-table {
  margin: 18px auto 0 auto;  /* centered on the page */
}

.map-col {
  width: 300px;
  min-width: 300px;
  max-width: 300px;
}

/* only the image cell loses its padding, so the column is exactly the image; the header keeps
   its own so the label is not flush against the border */
td.map-col {
  padding: 0 !important;
  line-height: 0;
}

.yard-preview {
  border: 1px solid rgba(128, 128, 128, 0.35);
  border-radius: 3px;
  vertical-align: middle;
  display: block;
}

.yard-legend {
  padding-top: 8px;
  font-size: 12px;
  opacity: 0.85;
}

.key {
  display: inline-block;
  width: 11px;
  height: 11px;
  margin: 0 4px 0 12px;
  vertical-align: -1px;
  border-width: 2px;
  border-style: solid;
}

.key-fence { border-color: #39b54a; background: rgba(57, 181, 74, 0.2); }
.key-circle { border-color: #3a7bd5; background: rgba(58, 123, 213, 0.15); border-radius: 50%; }

.yard-fleet {
  padding-top: 8px;
  font-size: 13px;
  word-break: break-word;
  text-align: left;
}

.copy-btn {
  float: left;
  margin: 0 8px 4px 0;
  line-height: 1;
}

</style>
